import {FilingTypeEnum, FilingFileExtention, CountryCodeEnum, ReturnDisplayType} from '../../utils/enums';
import getClientConfig from '../../utils/getClientConfig';

let helper = {};

// Define specific combinations of filing type and country that support Raw Form XML
const RAWFORM_XML_SUPPORTED_COMBINATIONS = [
    { filingType: FilingTypeEnum.VATRETURN, countryCode: "FR" },
    { filingType: FilingTypeEnum.OSSNONUNION, countryCode: "IE" },
    { filingType: FilingTypeEnum.VATRETURN, countryCode: "IE" }
];

const svrToWorkflowFrequenciesMap = {
    "Bi-Monthly": "Bi-monthly",
    "Half-Yearly": "Six-monthly",
    "Yearly": "Annually",
}

/**
 * Common methods
 */
const noOperation = () => {};
const FilingFrequencyName = {
    MONTHLY: 'Monthly'
};

const isMonthlyPeriod = (filingPeriodName) => typeof filingPeriodName === 'string' && filingPeriodName === FilingFrequencyName.MONTHLY;
const isXMLType = ({extension}={}) => typeof extension === 'string' && extension.trim().toUpperCase() === FilingFileExtention.XML.toUpperCase();
const isFixedFormat = (outputType) => typeof outputType === 'string' && outputType.trim().toUpperCase() === FilingFileExtention.FIXEDFORMAT.toUpperCase();
const isMonthlyFixedFormat = ({outputType, filingPeriodName}={}) => {
    return isFixedFormat(outputType) && isMonthlyPeriod(filingPeriodName);
}
const isFilingForCorrectionPeriod = (returnDisplayType) => {
    let correctionPeriodDisplayType = [ReturnDisplayType.SHOW_CORRECTIONS_GROUP_BY_PERIOD.value, ReturnDisplayType.SHOW_CORRECTIONS_GROUP_BY_PERIOD_INCLUDING_ORIGINAL_DOCUMENTS.value]
    return correctionPeriodDisplayType.includes(+returnDisplayType); 
}

const canUseSystemFileNameForCorrection = (systemFileName, returnDisplayType) => {
    return systemFileName && isFilingForCorrectionPeriod(returnDisplayType)
}
/***************************
 * Intrastat specific code
 ***************************/
const INTRASTAT_COUNTRIES_USING_SYSTEM_FILENAME = [
    CountryCodeEnum.DE, CountryCodeEnum.SE, CountryCodeEnum.IT
];
const INTRASTAT_COUNTRY_WISE_CHECKS = {};
INTRASTAT_COUNTRY_WISE_CHECKS[CountryCodeEnum.IT.name] = isMonthlyFixedFormat;
INTRASTAT_COUNTRY_WISE_CHECKS[CountryCodeEnum.DE.name] = isXMLType;
INTRASTAT_COUNTRY_WISE_CHECKS[CountryCodeEnum.SE.name] = isXMLType;

function intrastatFileNameGenerator({countryCode='', startDate='', endDate='', documentExtension='', systemFileName, outputType, filingPeriodName}={}) {
    let countryEnum = CountryCodeEnum.enumValueOf(countryCode);
    //Basic check which is common for all cases checks if country needs to support systemFileName
    let useSystemFileName =  systemFileName && countryEnum && INTRASTAT_COUNTRIES_USING_SYSTEM_FILENAME.includes(countryEnum);
    
    //If no country wise check is needed we need to return true
    let countryWiseCheck = (
        typeof INTRASTAT_COUNTRY_WISE_CHECKS[countryEnum?.name] === 'function' 
            ? INTRASTAT_COUNTRY_WISE_CHECKS[countryEnum?.name]({extension: documentExtension, outputType, filingPeriodName})
            : true);

    //To use system file name it should pass both country inclusion and country specific check
    if(useSystemFileName && countryWiseCheck) {
        return systemFileName;
    } else {
        //Defaults to standard format
        return `${FilingTypeEnum.INTRASTAT} ${countryCode} (${startDate}_${endDate})`;
    }
}

/**
 * VAT Return specific code
 */
const VAT_RETURN_COUNTRIES_USING_SYSTEM_FILENAME = [
    CountryCodeEnum.IT, CountryCodeEnum.HU
];
const VAT_RETURN_COUNTRY_WISE_CHECKS = {};

function vatReturnFileNameGenerator({countryCode='', startDate='', endDate='', vatReturnDataCountry='', documentExtension='', systemFileName, outputType, filingPeriodName, returnDisplayType}={}) {
    let countryEnum = CountryCodeEnum.enumValueOf(countryCode);
    //Basic check which is common for all cases checks if country needs to support systemFileName
    let useSystemFileName =  systemFileName && countryEnum && VAT_RETURN_COUNTRIES_USING_SYSTEM_FILENAME.includes(countryEnum);
    //If no country wise check is needed we need to return true
    let countryWiseCheck = (
        typeof VAT_RETURN_COUNTRY_WISE_CHECKS[countryEnum?.name] === 'function' 
            ? VAT_RETURN_COUNTRY_WISE_CHECKS[countryEnum?.name]({extension: documentExtension, outputType, filingPeriodName})
            : true);

     //To use system file name it should pass both country inclusion and country specific check
     if((useSystemFileName && countryWiseCheck) || canUseSystemFileNameForCorrection(systemFileName, returnDisplayType)) {
        return systemFileName;
    } else {
        //Defaults to standard format
        let prefixText = "VAT Return ";
        if(CountryCodeEnum.EE === countryEnum) {
            prefixText = "VAT "
        }
        return `${prefixText}${vatReturnDataCountry} (${startDate}_${endDate})`;
    }
}

/**
 * EC Listing specific code
 */
const ECL_COUNTRIES_USING_SYSTEM_FILENAME = [
    CountryCodeEnum.HU
];
const ECL_COUNTRY_WISE_CHECKS = {};

function eclFileNameGenerator({ countryCode = '', startDate = '', endDate = '', eclDataCountry = '', documentExtension = '', systemFileName, outputType, filingPeriodName, returnDisplayType } = {}) {
    const countryEnum = CountryCodeEnum.enumValueOf(countryCode);
    // Basic check which is common for all cases; checks if country needs to support systemFileName
    const useSystemFileName = systemFileName && countryEnum && ECL_COUNTRIES_USING_SYSTEM_FILENAME.includes(countryEnum);
    // If no country wise check is needed we need to return true
    const countryWiseCheck = (
        typeof ECL_COUNTRY_WISE_CHECKS[countryEnum?.name] === 'function'
            ? ECL_COUNTRY_WISE_CHECKS[countryEnum?.name]({ extension: documentExtension, outputType, filingPeriodName })
            : true);

    // To use system file name it should pass both country inclusion and country specific check
    if ((useSystemFileName && countryWiseCheck) || canUseSystemFileNameForCorrection(systemFileName, returnDisplayType)) {
        return systemFileName;
    } else {
        // Default to SVR standard format
        // Example: "EC Listing HU (2023-06-01_2023-06-30)"
        return `EC Listing ${eclDataCountry} (${startDate}_${endDate})`;
    }
}
  
/**********************************************
 * Additional report specific code goes here 
 *********************************************/

/**
 * Factory method that returns a filing name generator function for the given filing type
 * 
 * @param {*} filingType 
 */
helper.fileNameFactory = function (filingType) {
    switch(filingType) {
        case FilingTypeEnum.INTRASTAT:
            return intrastatFileNameGenerator;
        case FilingTypeEnum.VATRETURN:
            return vatReturnFileNameGenerator;
        case FilingTypeEnum.ECLISTING:
            return eclFileNameGenerator;
        // Add more cases for other report types
        default:
            return noOperation;
    }
}

helper.isFilingOptionSortingRequired = function(filingType, countryCode) {
    let sortingNeeded = false;
    let countryCodeEnum = CountryCodeEnum.enumValueOf(countryCode)
    if(FilingTypeEnum.VATRETURN === filingType && CountryCodeEnum.EE === countryCodeEnum) {
        sortingNeeded = true;
    }
    return sortingNeeded;
}

helper.isFilingForCorrectionPeriod = function(returnDisplayType) {
    return isFilingForCorrectionPeriod(returnDisplayType);
}

helper.isRawformXmlSupported = function (filingType, countryCode) {
    if (!(getClientConfig().featureFlags?.EnableRawformXml)) {
        return false;
    }
 
    // Check if the specific combination of filing type and country is supported
    return RAWFORM_XML_SUPPORTED_COMBINATIONS.some(combination => 
        combination.filingType === filingType && combination.countryCode === countryCode
    );
}

// ESF-978: GB PDF VAT Returns are closed locally in SVR only — they are not
// transmitted to HMRC. Shared by both the sync (FilingPreview) and async
// (FilingPage) Mark as Submitted / Close Return confirmation modals so the
// two flows cannot drift apart on this condition again.
helper.isGbPdfVatReturn = function (filingType, countryCode, outputType) {
    return countryCode === 'GB' && filingType === FilingTypeEnum.VATRETURN && outputType === 'PDF';
}

helper.updateRawformXmlOutputFileValue = async (rawformXmlBlob, newFileName) => {
    const xmlString = await rawformXmlBlob.text();
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
    const outputFileElement = xmlDoc.querySelector('OutputFile');
    if (outputFileElement) {
        outputFileElement.textContent = newFileName;
    }
    const modifiedXmlString = new XMLSerializer().serializeToString(xmlDoc);
    return new Blob([modifiedXmlString], { type: 'application/xml' });
}

helper.mapPayloadToWorkflowDto = (payload) => {
    if (svrToWorkflowFrequenciesMap[payload.frequency]) {
        payload.frequency = svrToWorkflowFrequenciesMap[payload.frequency];
    }
    return payload;
}

/* VRS-1493: Compute start date for filtered filing options for corrections.
If the country is enabled (currently SK) and a correction period exists, use correctionStartDate;
otherwise use query startDate. Returns YYYY-MM-DD or undefined.
*/
const COUNTRIES_USE_CORRECTION_START = [CountryCodeEnum.SK, CountryCodeEnum.PL];

helper.getStartDateForFilteredOptions = function (queryParams = {}) {
    const country = (queryParams.countryCode || '').toUpperCase();
    const countryEnum = CountryCodeEnum.enumValueOf(country);
    const inGroup = COUNTRIES_USE_CORRECTION_START.includes(countryEnum);
    const correctionPeriod = queryParams.selectedCorrectionPeriod;
    const correctionStart = correctionPeriod && correctionPeriod.correctionStartDate;
    const shouldUseCorrectionStart = inGroup && !!correctionStart;
    const finalStartDate = shouldUseCorrectionStart ? String(correctionStart).substring(0, 10) : queryParams.startDate;
    return finalStartDate;
}

export default helper;
  